import { Component, type ErrorInfo, type ReactNode, useCallback } from 'react'
import { Link, NavLink, Outlet, useSearchParams } from 'react-router'

import { Button } from '#app/components/ui/button.tsx'
import {
	ResponsiveOverlay,
	ResponsiveOverlayClose,
} from '#app/components/ui/responsive-overlay.tsx'
import { Typography } from '#app/components/ui/typography.tsx'
import { HelpCta } from '#app/routes/mocra+/_form+/components/help-cta.tsx'
import {
	FORM_STEPS,
	buildStepPath,
	formatStepOrder,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import {
	HelpDialogProvider,
	useHelpDialog,
} from '#app/routes/mocra+/_form+/help-dialog-context.tsx'
import { IntakeDraftProvider } from '#app/routes/mocra+/_form+/intake-context.tsx'
import { cn } from '#app/utils/misc.tsx'

type ErrorBoundaryProps = {
	readonly children: ReactNode
}

type ErrorBoundaryState = {
	hasError: boolean
	error: Error | null
}

class MocraFormErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = {
		hasError: false,
		error: null,
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {
			hasError: true,
			error,
		}
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error('[MoCRA form layout] uncaught error', { error, info })
	}

	private reload = () => {
		if (typeof window !== 'undefined') {
			window.location.reload()
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="border-destructive/40 bg-destructive/10 text-destructive space-y-4 rounded-md border p-4 text-sm">
					<p className="font-medium">
						We hit a problem while loading this MoCRA section.
					</p>
					{this.state.error?.message ? (
						<pre className="bg-background/80 text-muted-foreground max-h-48 overflow-auto rounded p-3 text-xs break-words whitespace-pre-wrap">
							{this.state.error.message}
						</pre>
					) : null}
					<Button variant="destructive" size="sm" onClick={this.reload}>
						Reload page
					</Button>
				</div>
			)
		}
		return this.props.children
	}
}

export default function MocraFormLayout() {
	return (
		<MocraFormErrorBoundary>
			<HelpDialogProvider>
				<IntakeDraftProvider>
					<MocraFormLayoutContent />
				</IntakeDraftProvider>
			</HelpDialogProvider>
		</MocraFormErrorBoundary>
	)
}

function MocraFormLayoutContent() {
	const [searchParams] = useSearchParams()
	const { isOpen, openHelpDialog, closeHelpDialog, setOpen } = useHelpDialog()

	const handleRequestHelp = useCallback(() => {
		openHelpDialog()
	}, [openHelpDialog])

	return (
		<div className="flex flex-col gap-8 pb-16 lg:flex-row">
			<aside className="lg:w-64">
				<div className="sticky top-24 space-y-4">
					<nav className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-2">
						{FORM_STEPS.map((step) => {
							const href = buildStepPath(step.path, searchParams)
							return (
								<NavLink
									key={step.id}
									to={href}
									className={({ isActive }) =>
										cn(
											'flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors',
											isActive
												? 'bg-primary/10 text-primary'
												: 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
										)
									}
								>
									<span className="text-muted-foreground font-mono text-xs tracking-tight">
										{formatStepOrder(step)}
									</span>
									<span className="text-muted-foreground leading-none">—</span>
									<span className="text-sm leading-none font-medium">
										{step.title}
									</span>
								</NavLink>
							)
						})}
					</nav>
					<HelpCta
						className="hidden lg:block"
						onRequestHelp={handleRequestHelp}
					/>
				</div>
			</aside>
			<main className="flex-1 space-y-8">
				<Outlet />
			</main>
			<div className="fixed inset-x-4 bottom-6 z-40 lg:hidden">
				<HelpCta className="shadow-lg" onRequestHelp={handleRequestHelp} />
			</div>
			<ResponsiveOverlay
				open={isOpen}
				onOpenChange={setOpen}
				title="Talk with our team"
				description="Book a quick working session and get answers while your intake stays open."
				className="flex w-full max-w-lg flex-col gap-6"
			>
				<div className="space-y-4">
					<Typography.Description className="text-sm">
						We’ll walk through your current section, unblock sticking points,
						and outline next steps to finish the MoCRA intake.
					</Typography.Description>
					<div className="space-y-2">
						<Button
							asChild
							className="w-full"
							onClick={() => {
								closeHelpDialog()
							}}
						>
							<Link
								to="https://calendly.com/mo-curigenx/30min"
								target="_blank"
								rel="noreferrer"
							>
								Schedule a call
							</Link>
						</Button>
						<ResponsiveOverlayClose>
							<Button variant="ghost" className="w-full">
								Maybe later
							</Button>
						</ResponsiveOverlayClose>
					</div>
				</div>
			</ResponsiveOverlay>
		</div>
	)
}
